import api from '@/api/clients/api';
import { DiscountHistoryHost, DiscountTemplate } from '@/features/hosts/api/types/discount';
import { Host } from '@/features/hosts/api/types/host';
import { PaginatedResponse } from '@/types/responses/paginated-response';

class HostsApi {
  async fetchHost(hostId: number): Promise<Host> {
    const { data } = await api.get<Host>(`/hosts/${hostId}`);

    return data;
  }

  async createHost(
    email: string,
    firstName: string,
    lastName: string,
    phoneNumber: string,
  ): Promise<void> {
    await api.post(`/hosts`, {
      email,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      phoneNumber: phoneNumber || undefined,
    });
  }

  async removeHost(hostId: number): Promise<void> {
    await api.delete(`/hosts/${hostId}`);
  }

  async updateHostDestinations(hostId: number, destinationIds: number[]): Promise<void> {
    const { data } = await api.put<void>(`/hosts/${hostId}/destinations`, { destinationIds });

    return data;
  }

  async assignSingleDestinationToHost(hostId: number, destinationId: number): Promise<void> {
    const { data } = await api.post<void>(`/hosts/${hostId}/destinations/${destinationId}`);

    return data;
  }

  async unassignSingleDestinationFromHost(hostId: number, destinationId: number): Promise<void> {
    const { data } = await api.delete<void>(`/hosts/${hostId}/destinations/${destinationId}`);

    return data;
  }

  async assignInviteToParkingForHost(hostId: number): Promise<void> {
    const { data } = await api.post<void>(`/hosts/${hostId}/parking`);

    return data;
  }

  async unassignInviteToParkingFromHost(hostId: number): Promise<void> {
    const { data } = await api.delete<void>(`/hosts/${hostId}/parking`);

    return data;
  }

  async fetchDiscountTemplates(): Promise<{ templates: DiscountTemplate[] }> {
    const { data } = await api.get<{ templates: DiscountTemplate[] }>(`/discount-templates`);

    return data;
  }

  async updateHostDiscountTemplates(
    hostId: number,
    discountTemplateIds: number[],
  ): Promise<DiscountTemplate[]> {
    const { data } = await api.put<DiscountTemplate[]>(`/hosts/${hostId}/discount-templates`, {
      discountTemplateIds,
    });

    return data;
  }

  async fetchHostWithSearch({
    page,
    perPage,
    email,
    billingGroupId,
    destinationGroupId,
  }: {
    page: number;
    perPage: number;
    email?: string;
    billingGroupId?: string;
    destinationGroupId?: string;
  }): Promise<PaginatedResponse<Host>> {
    const { data } = await api.get<PaginatedResponse<Host>>(
      `/hosts/search?page=${page}&perPage=${perPage}`,
      {
        params: {
          email,
          billingGroupId,
          destinationGroupId,
        },
      },
    );

    return data;
  }

  async fetchHostDiscountHistory(
    hostId: number,
    page: number,
    perPage: number,
  ): Promise<PaginatedResponse<DiscountHistoryHost>> {
    const { data } = await api.get<PaginatedResponse<DiscountHistoryHost>>(
      `/hosts/${hostId}/discount-history`,
      {
        params: {
          page,
          perPage,
        },
      },
    );

    return data;
  }

  async detachDestinationsFromHost(hostId: number, destinationIds: number[]): Promise<void> {
    await api.delete<void>(`/hosts/${hostId}/destinations`, {
      params: {
        destinationIds,
      },
    });
  }
}

export const hostsApi = new HostsApi();
